import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Phone, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminEnrollments() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchEnrollments = async () => {
    try {
      const response = await axios.get(`${API}/enrollments`, getAuthHeaders());
      setEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      toast.error('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return isoString;
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-dark font-display">Enrollment Requests</h1>
        <p className="text-slate-600 mt-2">View and manage student enrollment requests</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
          <p className="mt-4 text-slate-600">Loading enrollments...</p>
        </div>
      ) : enrollments.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-600">No enrollment requests yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {enrollments.map((enrollment) => (
            <div
              key={enrollment.enrollment_id}
              data-testid={`enrollment-item-${enrollment.enrollment_id}`}
              className="border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-brand-dark">{enrollment.name}</h3>
                  <div className="flex items-center text-sm text-slate-500 mt-1">
                    <Calendar className="h-4 w-4 mr-1" />
                    {formatDate(enrollment.timestamp)}
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-block bg-brand-blue/10 text-brand-blue px-3 py-1 rounded-full text-sm font-medium">
                    {enrollment.preferred_mode}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center text-slate-600">
                  <Mail className="h-5 w-5 mr-2 text-brand-blue" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{enrollment.email}</p>
                  </div>
                </div>

                <div className="flex items-center text-slate-600">
                  <Phone className="h-5 w-5 mr-2 text-brand-blue" />
                  <div>
                    <p className="text-xs text-slate-500">Phone</p>
                    <p className="font-medium">{enrollment.phone}</p>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500 mb-1">Course Interest</p>
                <p className="text-brand-dark font-medium">{enrollment.course_interest}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
